import type {
  GtDocument,
  GtRoom,
  GtSpeaker,
  GtTurn,
  GtTurnState,
  MeetingAttributionGtSnapshotTurn,
  PrefillOrigin,
} from '@/api/internal'

export const PREFILL_ORIGIN_LABEL: Record<PrefillOrigin, string> = {
  soniox: 'Soniox',
  inhouse: 'Chorus',
}

export const GT_ROSTER_MAX = 512
export const GT_NOTE_MAX_CODEPOINTS = 2000
export const GT_UNDO_STACK_MAX = 100
export const MAX_TURN_TEXT_CODEPOINTS = 20000
export const SPEAKER_NAME_MAX_CODEPOINTS = 120
export const GT_HOTKEY_MAX_CODEPOINTS = 8

export type GtDoc = GtDocument

/** A speaker as saved before rooms existed: `kind` / `room_id` absent. */
export type LegacyGtSpeaker = Omit<GtSpeaker, 'kind' | 'room_id'> & Partial<Pick<GtSpeaker, 'kind' | 'room_id'>>

/** A document as saved before rooms existed (older rows, older browser mirrors): no `rooms`, legacy speakers. */
export type LegacyGtDocument = Omit<GtDocument, 'speakers' | 'rooms'> & {
  speakers: LegacyGtSpeaker[]
  rooms?: GtRoom[]
}

export const emptyGtDoc = (): GtDoc => ({ speakers: [], turns: [], rooms: [] })

/** Assignable hotkeys in press order: digits first, then QWERTY letters left free by the other bindings. */
export const SPEAKER_HOTKEYS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'Q',
  'W',
  'T',
  'Y',
  'I',
  'O',
  'P',
  'F',
  'H',
  'L',
  'C',
  'V',
  'B',
  'M',
]

/** Non-person labels a reviewer can put on a turn; each is a roster speaker created on first use. */
export type GtLabel = 'unknown' | 'echo'

export const GT_LABELS: Record<GtLabel, { name: string; hotkey: string }> = {
  unknown: { name: 'Unknown', hotkey: 'U' },
  echo: { name: 'Echo', hotkey: 'X' },
}

export const labelOfSpeaker = (speaker: GtSpeaker): GtLabel | null => {
  const normalized = normalizeSpeakerName(speaker.name)
  const match = (Object.keys(GT_LABELS) as GtLabel[]).find(
    (label) => normalizeSpeakerName(GT_LABELS[label].name) === normalized
  )
  return match ?? null
}

const freeHotkey = (speakers: readonly GtSpeaker[]): string | null => {
  const taken = new Set(speakers.map((sp) => sp.hotkey))
  return SPEAKER_HOTKEYS.find((d) => !taken.has(d)) ?? null
}

/** Fills missing hotkeys from the free digits in roster order so every speaker stays assignable. */
export const withHotkeys = (speakers: readonly GtSpeaker[]): GtSpeaker[] => {
  const filled: GtSpeaker[] = [...speakers]
  filled.forEach((speaker, index) => {
    if (speaker.hotkey || labelOfSpeaker(speaker)) return
    const hotkey = freeHotkey(filled)
    if (hotkey) filled[index] = { ...speaker, hotkey }
  })
  return filled
}

/** Legacy speakers predate rooms; every one of them is a person. */
const withSpeakerKinds = (speakers: readonly LegacyGtSpeaker[]): GtSpeaker[] =>
  speakers.map((speaker) => ({ ...speaker, kind: speaker.kind ?? 'person', room_id: speaker.room_id ?? null }))

export const hydrateDoc = (document: GtDocument | LegacyGtDocument | undefined | null): GtDoc => ({
  speakers: withHotkeys(withSpeakerKinds(document?.speakers ?? [])),
  turns: [...(document?.turns ?? [])],
  rooms: [...(document?.rooms ?? [])],
})

export const roomsById = (doc: GtDoc): Map<string, GtRoom> => new Map(doc.rooms.map((room) => [room.id, room]))

export interface RoomVoiceOrdinal {
  ordinal: number
  voiceCount: number
}

/** Every room voice's 1-based position among its room's voices, in roster order (mirrors the server). */
export const roomVoiceOrdinals = (doc: GtDoc): Map<string, RoomVoiceOrdinal> => {
  const voicesByRoom = new Map<string, string[]>()
  for (const speaker of doc.speakers) {
    if (speaker.room_id === null) continue
    const voices = voicesByRoom.get(speaker.room_id) ?? []
    voices.push(speaker.id)
    voicesByRoom.set(speaker.room_id, voices)
  }
  const ordinals = new Map<string, RoomVoiceOrdinal>()
  for (const voices of Array.from(voicesByRoom.values())) {
    voices.forEach((speakerId, index) => ordinals.set(speakerId, { ordinal: index + 1, voiceCount: voices.length }))
  }
  return ordinals
}

/**
 * The name a speaker is shown, exported and scored under; mirrors the server's display_speaker_name and
 * production's room naming for unnamed voices; a reviewer-named voice uses `"<room> · <name>"`.
 */
export const displaySpeakerName = (
  speaker: GtSpeaker,
  rooms: ReadonlyMap<string, GtRoom>,
  ordinals: ReadonlyMap<string, RoomVoiceOrdinal>
): string => {
  const room = speaker.room_id === null ? undefined : rooms.get(speaker.room_id)
  if (!room) return speaker.name
  if (speaker.voice_name) return `${room.label} · ${speaker.voice_name}`
  const ordinal = ordinals.get(speaker.id)
  return ordinal && ordinal.voiceCount > 1 ? `${room.label} · voice ${ordinal.ordinal}` : room.label
}

export const roomOfSpeaker = (doc: GtDoc, speaker: GtSpeaker): GtRoom | null =>
  speaker.room_id === null ? null : (doc.rooms.find((room) => room.id === speaker.room_id) ?? null)

export const roomVoices = (doc: GtDoc, roomId: string): GtSpeaker[] =>
  doc.speakers.filter((speaker) => speaker.room_id === roomId)

/** A new room needs two voices; a single voice can only join a room that already exists. */
export const canGroupIntoRoom = (voiceCount: number, roomCount: number): boolean =>
  voiceCount >= 2 || (voiceCount === 1 && roomCount > 0)

/** Drops rooms no speaker belongs to any more (the server refuses an empty room). */
const pruneEmptyRooms = (doc: GtDoc): GtDoc => {
  const occupied = new Set(doc.speakers.flatMap((sp) => (sp.room_id === null ? [] : [sp.room_id])))
  const rooms = doc.rooms.filter((room) => occupied.has(room.id))
  return rooms.length === doc.rooms.length ? doc : { ...doc, rooms }
}

const asPerson = (speaker: GtSpeaker): GtSpeaker => {
  if (speaker.kind === 'person' && speaker.room_id === null) return speaker
  if (speaker.voice_name == null) return { ...speaker, kind: 'person', room_id: null }
  const { voice_name: name, ...rest } = speaker
  return { ...rest, name, kind: 'person', room_id: null }
}

// Built via the RegExp constructor, not a literal: the "u" flag on a literal requires an es2015+ target.
const CONTROL_FORMAT_RE = new RegExp('[\\p{Cc}\\p{Cf}]', 'gu')

const capCodepoints = (value: string, max: number): string => Array.from(value).slice(0, max).join('')

/** Mirrors aria-web's sanitize_speaker_map_name: whitespace normalize, strip Cc/Cf, collapse+trim, cap length. */
export const sanitizeSpeakerName = (raw: string): string => {
  const spaced = raw.replace(/\s+/g, ' ')
  const stripped = spaced.replace(CONTROL_FORMAT_RE, '')
  const collapsed = stripped.replace(/\s+/g, ' ').trim()
  return capCodepoints(collapsed, SPEAKER_NAME_MAX_CODEPOINTS)
}

/** Mirrors the server's normalize(name): sanitize -> NFKC -> casefold -> collapse whitespace. */
export const normalizeSpeakerName = (raw: string): string =>
  sanitizeSpeakerName(raw).normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim()

/** "Speaker", "Speaker 1", and "Speaker A" are unlabeled placeholders, not reviewer names. */
const PLACEHOLDER_SPEAKER_NAME_RE = /^speaker(?:\s+(?:\d+|[a-z]))?$/

export const isPlaceholderSpeakerName = (name: string): boolean =>
  PLACEHOLDER_SPEAKER_NAME_RE.test(normalizeSpeakerName(name))

/** Person name to keep as a room voice. Placeholder labels and cleared room voices stay unnamed (voice N). */
export const retainedVoiceName = (speaker: GtSpeaker): string | null => {
  if (speaker.voice_name) return speaker.voice_name
  if (speaker.kind === 'room' || labelOfSpeaker(speaker) || isPlaceholderSpeakerName(speaker.name)) return null
  return speaker.name
}

export interface RoomJoinPlan {
  /** voice_name to write per joiner; a joiner without an entry keeps its current one. */
  voiceNames: Map<string, string | null>
  /** Names joiners lose because another voice in the room keeps them. */
  droppedNames: string[]
}

/**
 * How joiners' names land in a room (`roomId` null for a new one), independent of roster order: names already in
 * the room win, then explicit names, then joiners' room-voice names, then their person names.
 */
export const planRoomJoin = (
  speakers: readonly GtSpeaker[],
  joinerIds: ReadonlySet<string>,
  roomId: string | null,
  explicit?: Readonly<Record<string, string | null>>
): RoomJoinPlan => {
  const taken = new Set<string>()
  const claim = (name: string): boolean => {
    const key = normalizeSpeakerName(name)
    if (!key || taken.has(key)) return false
    taken.add(key)
    return true
  }
  for (const sp of speakers) {
    if (roomId !== null && sp.room_id === roomId && sp.voice_name) claim(sp.voice_name)
  }
  const isExplicit = (sp: GtSpeaker): boolean => explicit !== undefined && sp.id in explicit
  const rank = (sp: GtSpeaker): number => (isExplicit(sp) ? 0 : sp.voice_name ? 1 : 2)
  const movers = speakers
    .filter((sp) => joinerIds.has(sp.id) && (roomId === null || sp.room_id !== roomId))
    .sort((a, b) => rank(a) - rank(b))
  const voiceNames = new Map<string, string | null>()
  const droppedNames: string[] = []
  for (const sp of movers) {
    const raw = isExplicit(sp) ? explicit?.[sp.id] : retainedVoiceName(sp)
    const name = raw ? sanitizeSpeakerName(raw) || null : null
    const kept = name !== null && claim(name)
    if (name !== null && !kept) droppedNames.push(name)
    const next = kept ? name : null
    if (isExplicit(sp) || next !== (sp.voice_name ?? null)) voiceNames.set(sp.id, next)
  }
  return { voiceNames, droppedNames }
}

export const roomDepartureHasNameCollision = (
  speakers: readonly GtSpeaker[],
  departingSpeakerIds: ReadonlySet<string>
): boolean => {
  const names = new Set<string>()
  for (const speaker of speakers) {
    const name = departingSpeakerIds.has(speaker.id) ? (speaker.voice_name ?? speaker.name) : speaker.name
    const normalized = normalizeSpeakerName(name)
    if (names.has(normalized)) return true
    names.add(normalized)
  }
  return false
}

/** Mirrors aria-web's _slugify: lowercase, collapse non [a-z0-9] runs to "_", trim leading/trailing "_". */
export const slugifySpeakerName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

/** A room exports under `slugifySpeakerName(label)`, so labels with one slug ("Great Hall", "Great-Hall") are one room. */
export const sameRoomKey = (a: string, b: string): boolean => slugifySpeakerName(a) === slugifySpeakerName(b)

const randomHex = (length: number): string => {
  const bytes = new Uint8Array(Math.ceil(length / 2))
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, length)
}

export const generateSpeakerId = (): string => randomHex(8)

/** Room ids share the speaker id scheme (8 hex chars, minted client-side, never reused). */
export const generateRoomId = generateSpeakerId

export const generateSplitSuffix = (existingIds: ReadonlySet<string>, sourceTurnId: string): string => {
  for (let attempt = 0; attempt < 32; attempt++) {
    const suffix = randomHex(4)
    if (!existingIds.has(`${sourceTurnId}:${suffix}`)) return suffix
  }
  return randomHex(4)
}

/** Two fresh ids for the halves of a split, both rooted at the source turn. */
export const splitTurnIds = (turns: readonly GtTurn[], sourceTurnId: string): [string, string] => {
  const existingIds = new Set(turns.map((t) => t.id))
  const suffixA = generateSplitSuffix(existingIds, sourceTurnId)
  existingIds.add(`${sourceTurnId}:${suffixA}`)
  const suffixB = generateSplitSuffix(existingIds, sourceTurnId)
  return [`${sourceTurnId}:${suffixA}`, `${sourceTurnId}:${suffixB}`]
}

export interface CaretSplit {
  atMs: number
  texts: [string, string]
}

/** Split point for a caret inside the edited text: time is placed proportionally to the caret's codepoint offset. */
export const caretSplit = (turn: GtTurn, text: string, caret: number): CaretSplit | null => {
  const before = text.slice(0, caret).trim()
  const after = text.slice(caret).trim()
  if (!before || !after) return null
  const total = Array.from(text).length
  const offset = Array.from(text.slice(0, caret)).length
  const atMs = turn.start_ms + Math.round(((turn.end_ms - turn.start_ms) * offset) / total)
  if (!(atMs > turn.start_ms && atMs < turn.end_ms)) return null
  return { atMs, texts: [before, after] }
}

/** Caret for the audio position inside a turn: the proportional inverse of caretSplit, snapped to the end of the word. */
export const playheadCaret = (turn: GtTurn, text: string, ms: number): number | null => {
  if (!(ms >= turn.start_ms && ms <= turn.end_ms) || turn.end_ms <= turn.start_ms) return null
  const codepoints = Array.from(text)
  const offset = Math.round((codepoints.length * (ms - turn.start_ms)) / (turn.end_ms - turn.start_ms))
  if (offset <= 0) return 0
  const index = codepoints.slice(0, offset).join('').length
  const nextSpace = text.slice(index).search(/\s/)
  return nextSpace < 0 ? text.length : index + nextSpace
}

const mergeUnique = (a: string[], b: string[]): string[] => Array.from(new Set([...a, ...b]))

export type GtSaveState = 'idle' | 'saving' | 'saved' | 'unsaved' | 'error' | 'conflict' | 'readonly'

export interface GtState {
  doc: GtDoc
  version: number
  baselineSha: string
  snapshotByTurnId: Record<string, MeetingAttributionGtSnapshotTurn>
  readOnly: boolean
  dirty: boolean
  editSeq: number
  undoStack: GtDoc[]
  redoStack: GtDoc[]
}

export const initialGtState = (): GtState => ({
  doc: emptyGtDoc(),
  version: 0,
  baselineSha: '',
  snapshotByTurnId: {},
  readOnly: true,
  dirty: false,
  editSeq: 0,
  undoStack: [],
  redoStack: [],
})

export type GtAction =
  | {
      type: 'hydrate'
      doc: GtDoc
      version: number
      sha: string
      turnsSnapshot: MeetingAttributionGtSnapshotTurn[]
      readOnly: boolean
    }
  | { type: 'setReadOnly'; readOnly: boolean }
  | { type: 'setSpeaker'; turnId: string; speakerId: string | null }
  | { type: 'setState'; turnId: string; state: GtTurnState; by: string; at: string }
  | { type: 'setText'; turnId: string; text: string }
  | { type: 'setNote'; turnId: string; note: string }
  | { type: 'split'; turnId: string; atMs: number; texts?: [string, string]; ids?: [string, string] }
  | { type: 'addSpeaker'; name: string }
  | {
      type: 'assignLabel'
      turnId: string
      label: GtLabel
      by: string
      at: string
    }
  | { type: 'renameSpeaker'; speakerId: string; name: string }
  | { type: 'nameRoomVoice'; speakerId: string; name: string | null }
  | { type: 'deleteSpeakers'; speakerIds: string[] }
  | {
      type: 'groupRoom'
      speakerIds: string[]
      label: string
      roomId?: string
      voiceNames?: Record<string, string | null>
    }
  | { type: 'renameRoom'; roomId: string; label: string }
  | { type: 'ungroupRoom'; roomId: string }
  | { type: 'leaveRoom'; speakerId: string }
  | { type: 'setRecorder'; speakerId: string | null }
  | { type: 'setHotkey'; speakerId: string; hotkey: string | null }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'markSaved'; version: number; sha: string; editSeq: number }
  | { type: 'restoreMirror'; doc: GtDoc }

const pushUndo = (state: GtState): GtDoc[] => [...state.undoStack, state.doc].slice(-GT_UNDO_STACK_MAX)

const withEdit = (state: GtState, nextDoc: GtDoc): GtState => ({
  ...state,
  doc: nextDoc,
  dirty: true,
  editSeq: state.editSeq + 1,
  undoStack: pushUndo(state),
  redoStack: [],
})

const mapTurn = (doc: GtDoc, turnId: string, fn: (turn: GtTurn) => GtTurn): GtDoc => {
  let changed = false
  const turns = doc.turns.map((turn) => {
    if (turn.id !== turnId) return turn
    const next = fn(turn)
    if (next !== turn) changed = true
    return next
  })
  return changed ? { ...doc, turns } : doc
}

export const gtReducer = (state: GtState, action: GtAction): GtState => {
  if (state.readOnly && !['hydrate', 'setReadOnly', 'markSaved'].includes(action.type)) return state

  switch (action.type) {
    case 'restoreMirror':
      return withEdit(state, action.doc)
    case 'hydrate': {
      const snapshotByTurnId: Record<string, MeetingAttributionGtSnapshotTurn> = {}
      for (const turn of action.turnsSnapshot) snapshotByTurnId[turn.turn_id] = turn
      return {
        doc: action.doc,
        version: action.version,
        baselineSha: action.sha,
        snapshotByTurnId,
        readOnly: action.readOnly,
        dirty: false,
        editSeq: 0,
        undoStack: [],
        redoStack: [],
      }
    }
    case 'setReadOnly':
      return { ...state, readOnly: action.readOnly }
    case 'markSaved':
      return {
        ...state,
        version: action.version,
        baselineSha: action.sha,
        dirty: action.editSeq === state.editSeq ? false : state.dirty,
      }
    case 'setSpeaker': {
      const turn = state.doc.turns.find((t) => t.id === action.turnId)
      if (!turn || turn.speaker_id === action.speakerId) return state
      const next = mapTurn(state.doc, action.turnId, (t) => ({ ...t, speaker_id: action.speakerId }))
      return withEdit(state, next)
    }
    case 'setState': {
      const turn = state.doc.turns.find((t) => t.id === action.turnId)
      if (!turn || turn.state === action.state) return state
      if (action.state === 'confirmed' && !turn.speaker_id) return state
      const reviewed = action.state !== null
      const next = mapTurn(state.doc, action.turnId, (t) => ({
        ...t,
        state: action.state,
        reviewed_by: reviewed ? action.by : null,
        reviewed_at: reviewed ? action.at : null,
      }))
      return withEdit(state, next)
    }
    case 'setText': {
      const turn = state.doc.turns.find((t) => t.id === action.turnId)
      if (!turn) return state
      const text = capCodepoints(action.text, MAX_TURN_TEXT_CODEPOINTS)
      if (text === turn.text) return state
      const blank = text.trim().length === 0
      const next = mapTurn(state.doc, action.turnId, (t) => ({
        ...t,
        text,
        state: blank ? null : t.state,
        reviewed_by: blank ? null : t.reviewed_by,
        reviewed_at: blank ? null : t.reviewed_at,
      }))
      return withEdit(state, next)
    }
    case 'setNote': {
      const turn = state.doc.turns.find((t) => t.id === action.turnId)
      if (!turn) return state
      const note = capCodepoints(action.note.trim(), GT_NOTE_MAX_CODEPOINTS) || null
      if (note === turn.note) return state
      return withEdit(
        state,
        mapTurn(state.doc, action.turnId, (t) => ({ ...t, note }))
      )
    }
    case 'split': {
      const turn = state.doc.turns.find((t) => t.id === action.turnId)
      if (!turn) return state
      if (!(action.atMs > turn.start_ms && action.atMs < turn.end_ms)) return state
      const [idA, idB] = action.ids ?? splitTurnIds(state.doc.turns, turn.source_turn_id)
      const [textA, textB] = action.texts ?? [turn.text, turn.text]
      const first: GtTurn = { ...turn, id: idA, end_ms: action.atMs, text: textA }
      const second: GtTurn = {
        ...turn,
        id: idB,
        start_ms: action.atMs,
        text: textB,
        state: null,
        note: null,
        reviewed_by: null,
        reviewed_at: null,
      }
      const turns = state.doc.turns.flatMap((t) => (t.id === action.turnId ? [first, second] : [t]))
      return withEdit(state, { ...state.doc, turns })
    }
    case 'addSpeaker': {
      const name = sanitizeSpeakerName(action.name)
      if (!name) return state
      if (state.doc.speakers.length >= GT_ROSTER_MAX) return state
      const normalized = normalizeSpeakerName(name)
      if (state.doc.speakers.some((sp) => normalizeSpeakerName(sp.name) === normalized)) return state
      const hotkey = freeHotkey(state.doc.speakers)
      const speaker: GtSpeaker = {
        id: generateSpeakerId(),
        name,
        hotkey,
        is_recorder: false,
        source_person_ids: [],
        source_refined_speaker_ids: [],
        kind: 'person',
        room_id: null,
      }
      return withEdit(state, { ...state.doc, speakers: [...state.doc.speakers, speaker] })
    }
    case 'assignLabel': {
      const turn = state.doc.turns.find((t) => t.id === action.turnId)
      if (!turn) return state
      const { name, hotkey } = GT_LABELS[action.label]
      const normalized = normalizeSpeakerName(name)
      let speaker = state.doc.speakers.find((sp) => normalizeSpeakerName(sp.name) === normalized)
      let speakers = state.doc.speakers
      if (!speaker) {
        if (speakers.length >= GT_ROSTER_MAX) return state
        speaker = {
          id: generateSpeakerId(),
          name,
          hotkey: speakers.some((sp) => sp.hotkey === hotkey) ? null : hotkey,
          is_recorder: false,
          source_person_ids: [],
          source_refined_speaker_ids: [],
          kind: 'person',
          room_id: null,
        }
        speakers = [...speakers, speaker]
      }
      if (turn.speaker_id === speaker.id && turn.state === 'confirmed') return state
      const speakerId = speaker.id
      const turns = state.doc.turns.map((t) =>
        t.id === action.turnId
          ? {
              ...t,
              speaker_id: speakerId,
              state: 'confirmed' as const,
              reviewed_by: action.by,
              reviewed_at: action.at,
            }
          : t
      )
      return withEdit(state, { ...state.doc, speakers, turns })
    }
    case 'renameSpeaker': {
      const name = sanitizeSpeakerName(action.name)
      if (!name) return state
      const source = state.doc.speakers.find((sp) => sp.id === action.speakerId)
      if (!source) return state
      const normalized = normalizeSpeakerName(name)
      const target = state.doc.speakers.find(
        (sp) => sp.id !== action.speakerId && normalizeSpeakerName(sp.name) === normalized
      )
      if (!target) {
        if (source.name === name) return state
        return withEdit(state, {
          ...state.doc,
          speakers: state.doc.speakers.map((sp) => (sp.id === source.id ? { ...sp, name } : sp)),
        })
      }
      const merged: GtSpeaker = {
        ...target,
        hotkey: target.hotkey ?? source.hotkey,
        is_recorder: target.is_recorder || source.is_recorder,
        source_person_ids: mergeUnique(target.source_person_ids, source.source_person_ids),
        source_refined_speaker_ids: mergeUnique(target.source_refined_speaker_ids, source.source_refined_speaker_ids),
      }
      return withEdit(
        state,
        pruneEmptyRooms({
          ...state.doc,
          speakers: state.doc.speakers
            .filter((sp) => sp.id !== source.id)
            .map((sp) => (sp.id === target.id ? merged : sp)),
          turns: state.doc.turns.map((t) => (t.speaker_id === source.id ? { ...t, speaker_id: target.id } : t)),
        })
      )
    }
    case 'nameRoomVoice': {
      const speaker = state.doc.speakers.find((sp) => sp.id === action.speakerId)
      if (!speaker || speaker.room_id === null) return state
      const name = action.name ? sanitizeSpeakerName(action.name) || null : null
      if ((speaker.voice_name ?? null) === name) return state
      return withEdit(state, {
        ...state.doc,
        speakers: state.doc.speakers.map((sp) => (sp.id === speaker.id ? { ...sp, voice_name: name } : sp)),
      })
    }
    case 'deleteSpeakers': {
      const wanted = new Set(action.speakerIds.filter((id) => state.doc.speakers.some((sp) => sp.id === id)))
      if (wanted.size === 0) return state
      const speakers = withHotkeys(state.doc.speakers.filter((sp) => !wanted.has(sp.id)))
      const turns = state.doc.turns.map((t) => {
        if (!t.speaker_id || !wanted.has(t.speaker_id)) return t
        // The server rejects a confirmed turn without a speaker; a deleted turn keeps its state.
        if (t.state === 'confirmed')
          return { ...t, speaker_id: null, state: null, reviewed_by: null, reviewed_at: null }
        return { ...t, speaker_id: null }
      })
      return withEdit(state, pruneEmptyRooms({ ...state.doc, speakers, turns }))
    }
    case 'groupRoom': {
      const label = sanitizeSpeakerName(action.label)
      if (!label) return state
      const wanted = new Set(action.speakerIds)
      // Labels (Unknown / Echo) are abstentions, not voices; they never join a room.
      const voices = state.doc.speakers.filter((sp) => wanted.has(sp.id) && labelOfSpeaker(sp) === null)
      if (voices.length === 0) return state
      const existing = state.doc.rooms.find((room) => sameRoomKey(room.label, label))
      if (!existing && state.doc.rooms.length >= GT_ROSTER_MAX) return state
      if (!existing && action.voiceNames) {
        const names = voices.map((sp) => normalizeSpeakerName(action.voiceNames?.[sp.id] ?? '')).filter(Boolean)
        if (new Set(names).size !== names.length) return state
      }
      const room: GtRoom = existing ?? { id: action.roomId ?? generateRoomId(), label }
      if (voices.every((sp) => sp.room_id === room.id)) return state
      const voiceIds = new Set(voices.map((sp) => sp.id))
      const plan = planRoomJoin(
        state.doc.speakers,
        voiceIds,
        existing?.id ?? null,
        existing ? undefined : action.voiceNames
      )
      const speakers = state.doc.speakers.map((sp) => {
        if (!voiceIds.has(sp.id)) return sp
        const voiceName = plan.voiceNames.get(sp.id)
        return {
          ...sp,
          kind: 'room' as const,
          room_id: room.id,
          ...(voiceName !== undefined && { voice_name: voiceName }),
        }
      })
      const rooms = existing ? state.doc.rooms : [...state.doc.rooms, room]
      return withEdit(state, pruneEmptyRooms({ ...state.doc, speakers, rooms }))
    }
    case 'renameRoom': {
      const label = sanitizeSpeakerName(action.label)
      if (!label) return state
      const room = state.doc.rooms.find((r) => r.id === action.roomId)
      if (!room || room.label === label) return state
      const target = state.doc.rooms.find((r) => r.id !== room.id && sameRoomKey(r.label, label))
      if (!target) {
        return withEdit(state, {
          ...state.doc,
          rooms: state.doc.rooms.map((r) => (r.id === room.id ? { ...r, label } : r)),
        })
      }
      // Renaming onto another room's label merges the two rooms' voices, like renameSpeaker does for speakers.
      return withEdit(state, {
        ...state.doc,
        speakers: state.doc.speakers.map((sp) => (sp.room_id === room.id ? { ...sp, room_id: target.id } : sp)),
        rooms: state.doc.rooms.filter((r) => r.id !== room.id),
      })
    }
    case 'ungroupRoom': {
      if (!state.doc.rooms.some((room) => room.id === action.roomId)) return state
      const departingIds = new Set(state.doc.speakers.filter((sp) => sp.room_id === action.roomId).map((sp) => sp.id))
      if (roomDepartureHasNameCollision(state.doc.speakers, departingIds)) return state
      return withEdit(state, {
        ...state.doc,
        speakers: state.doc.speakers.map((sp) => (sp.room_id === action.roomId ? asPerson(sp) : sp)),
        rooms: state.doc.rooms.filter((room) => room.id !== action.roomId),
      })
    }
    case 'leaveRoom': {
      const speaker = state.doc.speakers.find((sp) => sp.id === action.speakerId)
      if (!speaker || speaker.room_id === null) return state
      if (roomDepartureHasNameCollision(state.doc.speakers, new Set([speaker.id]))) return state
      return withEdit(
        state,
        pruneEmptyRooms({
          ...state.doc,
          speakers: state.doc.speakers.map((sp) => (sp.id === speaker.id ? asPerson(sp) : sp)),
        })
      )
    }
    case 'setRecorder': {
      let changed = false
      const speakers = state.doc.speakers.map((sp) => {
        const isRecorder = sp.id === action.speakerId
        if (isRecorder !== sp.is_recorder) changed = true
        return isRecorder === sp.is_recorder ? sp : { ...sp, is_recorder: isRecorder }
      })
      if (!changed) return state
      return withEdit(state, { ...state.doc, speakers })
    }
    case 'setHotkey': {
      const speaker = state.doc.speakers.find((sp) => sp.id === action.speakerId)
      if (!speaker) return state
      const hotkey = action.hotkey ? capCodepoints(action.hotkey.trim(), GT_HOTKEY_MAX_CODEPOINTS) || null : null
      if (hotkey === speaker.hotkey) return state
      return withEdit(state, {
        ...state.doc,
        speakers: state.doc.speakers.map((sp) => (sp.id === speaker.id ? { ...sp, hotkey } : sp)),
      })
    }
    case 'undo': {
      if (state.undoStack.length === 0) return state
      const previous = state.undoStack[state.undoStack.length - 1]
      return {
        ...state,
        doc: previous,
        dirty: true,
        editSeq: state.editSeq + 1,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, state.doc].slice(-GT_UNDO_STACK_MAX),
      }
    }
    case 'redo': {
      if (state.redoStack.length === 0) return state
      const next = state.redoStack[state.redoStack.length - 1]
      return {
        ...state,
        doc: next,
        dirty: true,
        editSeq: state.editSeq + 1,
        undoStack: [...state.undoStack, state.doc].slice(-GT_UNDO_STACK_MAX),
        redoStack: state.redoStack.slice(0, -1),
      }
    }
    default:
      return state
  }
}
